/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import isEqual from 'lodash/isEqual';
import { useToasts } from '@scope/scope-one-ui/core/Messages/context';
import { ErrorMessage } from 'shared/types/errorMessage';
import { RequestOptions, useApi } from './useApi';

export const useGet = <T>(url: string, options?: RequestOptions<void>) => {
  const { get } = useApi();

  /* Local request state  */
  const [path, setPath] = useState(url);
  const [requestOptions, setRequestOptions] = useState(options);
  const [data, setData] = useState<T>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ErrorMessage | null>(null);

  /* Requests the data, managing loading state and error if thrown */
  const refetch = () => {
    setLoading(true);
    get<T>(path, requestOptions)
      .then((res) => {
        setData(res);
        setError(null);
      })
      .catch((err) => setError(JSON.parse(err.message)))
      .finally(() => setLoading(false));
  };

  /* Calls request and updates local state when parameters change */
  useEffect(() => {
    if (requestOptions?.disableAutofetch) {
      return;
    }

    refetch();
  }, [path, requestOptions]);

  /* Checks the equality between url and options, and updates state if different */
  useEffect(() => {
    setPath(url);

    if (!isEqual(options, requestOptions)) {
      setRequestOptions(options);
    }
  }, [url, options]);

  return { data, loading, error, refetch };
};

const makeRestHook =
  (method: 'get' | 'post' | 'put' | 'del') =>
  <P, R = void>(func: (payload: P) => { url: string; options?: RequestOptions }) =>
  () => {
    const api = useApi();
    const { error } = useToasts();

    return (payload: P) => {
      const { url, options } = func(payload);

      return api[method]<R, P>(url, options).catch((err) => {
        const stack: ErrorMessage = JSON.parse(err.message);
        error({
          title: stack.status,
          content: stack.message,
        });
        throw err;
      });
    };
  };

export const usePost = makeRestHook('post');

export const usePut = makeRestHook('put');

export const useDelete = makeRestHook('del');

export const useFetch = makeRestHook('get');
